import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';

const emailValidationSchema = Yup.object({
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
});

const resetValidationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
    .min(6, 'Code must be at least 6 characters'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters'),
});

const ForgotPassword: React.FC = () => {
  const { resetPassword, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [resetSent, setResetSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');

  const emailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: emailValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setErrorMsg('');
      try {
        await resetPassword(values.email);
        setEmail(values.email);
        setResetSent(true);
      } catch (error: any) {
        console.error('Password reset error:', error);
        let errorMessage = 'Failed to send reset email. Please try again.';
        
        if (error.code === 'UserNotFoundException' || error.name === 'UserNotFoundException' || error.code === 'auth/user-not-found') {
          errorMessage = 'No account with this email address was found.';
        }
        
        setErrorMsg(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const resetFormik = useFormik({
    initialValues: {
      code: '',
      newPassword: '',
    },
    validationSchema: resetValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setErrorMsg('');
      try {
        await confirmPasswordReset(email, values.code, values.newPassword);
        setResetSuccess(true);
      } catch (error: any) {
        console.error('Password confirm error:', error);
        setErrorMsg(error.message || 'Failed to reset password. Please check your code and try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (resetSuccess) {
    return (
      <Box>
        <Typography variant="h5" fontWeight="medium" gutterBottom>
          Password Reset Complete
        </Typography>
        <Alert severity="success" sx={{ mb: 3, mt: 2 }}>
          Your password has been successfully reset. You can now log in with your new password.
        </Alert>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => navigate('/login')}
          sx={{
            py: 1.5,
            mt: 2,
            fontWeight: 'medium',
            backgroundImage: 'var(--primary-gradient)',
          }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="medium" gutterBottom>
        {resetSent ? 'Check Your Email' : 'Reset Password'}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        {resetSent 
          ? `We've sent a 6-digit verification code to ${email}. Please enter it below along with your new password.`
          : "Enter your email address and we'll send you a verification code to reset your password."}
      </Typography>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMsg}
        </Alert>
      )}

      {resetSent ? (
        <form onSubmit={resetFormik.handleSubmit}>
          <TextField
            fullWidth
            id="code"
            name="code"
            label="6-Digit Verification Code"
            variant="outlined"
            margin="normal"
            value={resetFormik.values.code}
            onChange={resetFormik.handleChange}
            onBlur={resetFormik.handleBlur}
            error={resetFormik.touched.code && Boolean(resetFormik.errors.code)}
            helperText={resetFormik.touched.code && resetFormik.errors.code}
          />
          <TextField
            fullWidth
            id="newPassword"
            name="newPassword"
            label="New Password"
            type="password"
            variant="outlined"
            margin="normal"
            value={resetFormik.values.newPassword}
            onChange={resetFormik.handleChange}
            onBlur={resetFormik.handleBlur}
            error={resetFormik.touched.newPassword && Boolean(resetFormik.errors.newPassword)}
            helperText={resetFormik.touched.newPassword && resetFormik.errors.newPassword}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              mt: 3,
              mb: 3,
              fontWeight: 'medium',
              backgroundImage: 'var(--primary-gradient)',
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
          </Button>
          <Box textAlign="center">
            <Link
              component="button"
              type="button"
              onClick={() => {
                setResetSent(false);
                setErrorMsg('');
                resetFormik.resetForm();
              }}
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              Back
            </Link>
          </Box>
        </form>
      ) : (
        <form onSubmit={emailFormik.handleSubmit}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            variant="outlined"
            margin="normal"
            value={emailFormik.values.email}
            onChange={emailFormik.handleChange}
            onBlur={emailFormik.handleBlur}
            error={emailFormik.touched.email && Boolean(emailFormik.errors.email)}
            helperText={emailFormik.touched.email && emailFormik.errors.email}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              mt: 3,
              mb: 3,
              fontWeight: 'medium',
              backgroundImage: 'var(--primary-gradient)',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send Code'}
          </Button>
          <Box textAlign="center">
            <Link
              component={RouterLink}
              to="/login"
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              Back to Login
            </Link>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default ForgotPassword;